import { ArrowBack as ArrowBackIcon, Close as CloseIcon } from "@mui/icons-material";
import PersonIcon from "@mui/icons-material/Person";
import SendIcon from "@mui/icons-material/Send";
import { Box, IconButton, Skeleton, Tooltip } from "@mui/material";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useIsScrollbarVisible } from "../../../hooks/useIsScrollbarVisible";
import { useIsMobile } from "../../../hooks/useMobile";
import { isNurseActiveAnywhere } from "../../../utils/formatters";
import type { MessageStatus, Nurse, UserSentMessage } from "../../../utils/models";
import ColorfulRole from "../../UIToolkit/ColorfulRole/ColorfulRole";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { getSmsMaxChars } from "./CharCounter";
import { CommunicationStatusToIcon } from "./CommunicationStatusToIcon";
import CommunicationTextField from "./CommunicationTextField";
import {
	NewMessageRootDiv,
	NewThreadMessageDiv,
	ThreadMessageHeader,
	ThreadMessageList,
	ThreadMessageListItem,
	ThreadMessageListSection,
	ThreadMessagesListDiv,
	ThreadMessagesRootDiv,
} from "./styled";
import { failedStatusToString } from "./utils";

const CommunicationThread: FC<{
	threads: UserSentMessage[];
	nurse: Nurse;
	clearUserSelection: () => void;
	postNewMessage: (message: string) => void;
	hasOlderMessages?: boolean;
	loadOlderMessages?: () => void;
	isLoadingOlderMessages?: boolean;
	isThreadLoading?: boolean;
}> = ({ threads, nurse, clearUserSelection, postNewMessage, hasOlderMessages, loadOlderMessages, isLoadingOlderMessages, isThreadLoading }) => {
	const { getNurseFormattedName, chosenDepartment } = useHospitalData();
	const { t } = useTranslation();
	const { isMobile } = useIsMobile();

	const navigate = useNavigate();
	const threadEndRef = useRef<HTMLLIElement>(null);
	const threadsRef = useRef<HTMLOListElement>(null);
	const previousScrollHeightRef = useRef<number | null>(null);
	const sortedThread = useMemo(() => [...threads].sort((a, b) => a.dateCreated.valueOf() - b.dateCreated.valueOf()), [threads]);
	useEffect(() => {
		const timeout = setTimeout(() => {
			const messageList = threadsRef.current;
			const previousScrollHeight = previousScrollHeightRef.current;
			if (messageList && previousScrollHeight !== null) {
				messageList.scrollTop += messageList.scrollHeight - previousScrollHeight;
				previousScrollHeightRef.current = null;
				return;
			}
			threadEndRef.current?.scrollIntoView(
				threads.length <= 20
					? {
							behavior: "smooth",
						}
					: undefined,
			);
		}, 0); // append the scroll command to the end of the event loop
		return () => {
			clearTimeout(timeout);
		};
	}, [threads]);

	const headerContainerRef = useRef<HTMLDivElement>(null);
	const scrollbarVisible = useIsScrollbarVisible(threadsRef);

	const [newMessage, setNewMessage] = useState<string>("");

	const handleNewMessageChange = useCallback((value: string) => {
		setNewMessage(value);
	}, []);

	const smsMaxChars = getSmsMaxChars(newMessage);
	const isOverSmsLimit = newMessage.length > smsMaxChars;

	const handleSendMessage = useCallback(() => {
		if (isNurseActiveAnywhere(nurse) && newMessage && !isOverSmsLimit) {
			postNewMessage(newMessage);
			setNewMessage("");
		}
	}, [newMessage, postNewMessage, nurse, isOverSmsLimit]);
	const handleLoadOlderMessages = useCallback(() => {
		if (!threadsRef.current || !loadOlderMessages) return;
		previousScrollHeightRef.current = threadsRef.current.scrollHeight;
		loadOlderMessages();
	}, [loadOlderMessages]);

	return (
		<ThreadMessagesRootDiv>
			<ThreadMessageHeader ref={headerContainerRef}>
				{/* Mobile is a full-page view (no surrounding drawer), so a leading back arrow is the
				    expected way to return to the thread list. Desktop keeps the trailing close button below. */}
				{isMobile && (
					<IconButton
						aria-label="Back to messages"
						edge="start"
						onClick={clearUserSelection}
						sx={{ transition: "transform 160ms ease-out", "&:active": { transform: "scale(0.92)" } }}
					>
						<ArrowBackIcon />
					</IconButton>
				)}
				<ColorfulRole role={nurse.role} />
				<Tooltip title={getNurseFormattedName(nurse.id)}>
					<span>{getNurseFormattedName(nurse.id)}</span>
				</Tooltip>
				<Tooltip title={"Open profile"}>
					<IconButton
						onClick={(_) => {
							const path = `/${chosenDepartment.department_id}/nurse/${nurse.id}`;
							navigate(path, {
								state: { navigationSrc: window.location.pathname },
							});
						}}
					>
						<PersonIcon style={{ fontSize: "16px" }} />
					</IconButton>
				</Tooltip>
				{!isNurseActiveAnywhere(nurse) && <span style={{ color: "red" }}>Inactive Nurse</span>}

				<span style={{ flexGrow: 1 }} />
				{!isMobile && (
					<IconButton aria-label="Close conversation" onClick={clearUserSelection}>
						<CloseIcon />
					</IconButton>
				)}
			</ThreadMessageHeader>
			<ThreadMessageListSection
				style={{
					height: `calc(100% - ${headerContainerRef?.current?.clientHeight ?? 0}px)`,
				}}
			>
				<ThreadMessagesListDiv>
					<ThreadMessageList ref={threadsRef}>
						{isThreadLoading && <Skeleton variant="rounded" width="100%" height="70px" />}
						{hasOlderMessages && loadOlderMessages && (
							<li>
								<SystemButton
									buttonVariant="borderless"
									loading={isLoadingOlderMessages}
									onClick={handleLoadOlderMessages}
									data-testid="staff-communication-load-older-button"
								>
									{t("staff.communication.load_older")}
								</SystemButton>
							</li>
						)}
						{sortedThread.map((currMessage) => {
							const isPushNotification = "isPush" in currMessage && currMessage.isPush;
							const messageTime = currMessage.dateCreated;

							return (
								<ThreadMessageListItem
									key={currMessage.tempId ?? currMessage.id}
									sentFrom={isPushNotification ? "server" : currMessage.direction === "inbound" ? "user" : currMessage.senderUserId ? "admin" : "server"}
								>
									<span
										style={{
											fontWeight: 400,
											overflowWrap: "anywhere",
											whiteSpace: "pre-wrap",
										}}
									>
										{currMessage.body}
									</span>
									<Box
										style={{ fontSize: 12, cursor: "default" }}
										sx={{
											display: "flex",
											justifyContent: "space-between",
										}}
									>
										<Tooltip
											title={
												messageTime
													? `${messageTime.toJSDate().toLocaleDateString()} ${messageTime.toJSDate().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} ${currMessage.status}`
													: ""
											}
										>
											<Box
												sx={{
													display: "flex",
													gap: "4px",
													alignItems: "center",
												}}
											>
												<span style={{ opacity: 0.5 }}>{messageTime?.toRelative() || ""}</span>
												{currMessage.status && <CommunicationStatusToIcon status={currMessage.status as MessageStatus} />}
											</Box>
										</Tooltip>
										{isPushNotification ? (
											<span style={{ opacity: 0.5 }}>Sent by In-House (push)</span>
										) : failedStatusToString(currMessage.status as MessageStatus) ? (
											<span style={{ color: "red", opacity: 0.5 }}>{failedStatusToString(currMessage.status as MessageStatus)}</span>
										) : currMessage.direction === "outbound" ? (
											<span style={{ opacity: 0.5 }}>{`Sent by ${currMessage.senderFirstName ?? "In-House"} (SMS)`}</span>
										) : null}
									</Box>
								</ThreadMessageListItem>
							);
						})}

						<li key="thread-end-marker" ref={threadEndRef} />
					</ThreadMessageList>

					{
						<NewMessageRootDiv
							style={{
								marginRight: scrollbarVisible ? "8px" : "4px",
							}}
						>
							<NewThreadMessageDiv>
								<CommunicationTextField
									value={newMessage}
									onChange={handleNewMessageChange}
									onEnter={handleSendMessage}
									disabled={!isNurseActiveAnywhere(nurse)}
									maxChars={smsMaxChars}
								/>
							</NewThreadMessageDiv>
							<IconButton onClick={handleSendMessage} sx={{ height: "fit-content" }} disabled={!newMessage || isOverSmsLimit}>
								<SendIcon />
							</IconButton>
						</NewMessageRootDiv>
					}
				</ThreadMessagesListDiv>
			</ThreadMessageListSection>
		</ThreadMessagesRootDiv>
	);
};

export default CommunicationThread;
